import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Funnel, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { ProductCard } from '@/components/product/ProductCard';
import { motion, AnimatePresence } from 'framer-motion';
import { PRODUCTS, CATEGORIES } from '@/data/products';

const categoryImages = {
  'Wall Art': 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=1600',
  'Decorative Lamps': 'https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=1600',
  'Throw Pillows': 'https://images.unsplash.com/photo-1629949009765-40fc74c9ec21?w=1600',
  'Vases & Planters': 'https://images.unsplash.com/photo-1612871177858-b5defed7c67c?w=1600',
  'Home Fragrances': 'https://images.unsplash.com/photo-1602028915047-37269d1a73f7?w=1600',
  'Table Decor': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1600',
  'Mirrors': 'https://images.unsplash.com/photo-1618220179428-22790b461013?w=1600',
};

export default function CategoryPage() {
  const { categoryName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Filter states
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');

  // Filter and sort products using static data
  const filteredProducts = useMemo(() => {
    let products = [...PRODUCTS];
    
    // Filter by category
    if (categoryName) {
      products = products.filter(p => p.category === categoryName);
    }
    
    // Filter by price
    products = products.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    
    // Sort
    switch (sortBy) {
      case 'price_low':
        products.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        products.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        products.sort((a, b) => b.rating - a.rating);
        break;
      default:
        // newest - keep original order
        break;
    }
    
    return products;
  }, [categoryName, priceRange, sortBy]);

  const handleSortChange = (value) => {
    setSortBy(value);
    setSearchParams({ sort: value });
  };

  const bannerImage = categoryName 
    ? categoryImages[categoryName] || categoryImages['Wall Art']
    : 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1600';

  const pageTitle = categoryName || 'All Products';

  return (
    <div data-testid="category-page">
      {/* Banner */}
      <div className="relative h-64 md:h-80 overflow-hidden" data-testid="category-banner">
        <img 
          src={bannerImage}
          alt={pageTitle}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-nexa-charcoal/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-medium text-white mb-2">
              {pageTitle}
            </h1>
            <p className="text-white/80">
              {filteredProducts.length} Products
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <Button 
            variant="outline"
            className="lg:hidden"
            onClick={() => setFiltersOpen(true)}
            data-testid="open-filters-btn"
          >
            <Funnel size={18} className="mr-2" />
            Filters
          </Button>

          <div className="flex items-center gap-4 ml-auto">
            <span className="text-sm text-muted-foreground hidden md:block">Sort by:</span>
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-48" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Best Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-12">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-testid="desktop-filters">
            <div className="sticky top-32 space-y-8">
              <div>
                <h3 className="font-semibold text-nexa-charcoal mb-4">Categories</h3>
                <div className="space-y-3">
                  {CATEGORIES.map((cat) => (
                    <label key={cat} className="flex items-center gap-2 cursor-pointer group">
                      <Checkbox 
                        checked={categoryName === cat}
                        onCheckedChange={() => {
                          if (categoryName === cat) {
                            window.location.href = '/shop';
                          } else {
                            window.location.href = `/category/${cat}`;
                          }
                        }}
                      />
                      <span className="text-sm text-muted-foreground group-hover:text-nexa-charcoal transition-colors">
                        {cat}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-nexa-charcoal mb-4">Price Range</h3>
                <Slider
                  min={0}
                  max={500}
                  step={10}
                  value={priceRange}
                  onValueChange={setPriceRange}
                  className="mb-4"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>${priceRange[0]}</span>
                  <span>${priceRange[1]}+</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile Filters */}
          <AnimatePresence>
            {filtersOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 lg:hidden"
              >
                <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
                <motion.div
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100%' }}
                  className="absolute left-0 top-0 bottom-0 w-80 bg-white p-6 overflow-y-auto"
                  data-testid="mobile-filters"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-serif text-xl font-semibold">Filters</h2>
                    <button onClick={() => setFiltersOpen(false)}>
                      <X size={24} />
                    </button>
                  </div>

                  <div className="space-y-8">
                    <div>
                      <h3 className="font-semibold text-nexa-charcoal mb-4">Categories</h3>
                      <div className="space-y-3">
                        {CATEGORIES.map((cat) => (
                          <label key={cat} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox 
                              checked={categoryName === cat}
                              onCheckedChange={() => {
                                setFiltersOpen(false);
                                if (categoryName === cat) {
                                  window.location.href = '/shop';
                                } else {
                                  window.location.href = `/category/${cat}`;
                                }
                              }}
                            />
                            <span className="text-sm">{cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-nexa-charcoal mb-4">Price Range</h3>
                      <Slider
                        min={0}
                        max={500}
                        step={10}
                        value={priceRange}
                        onValueChange={setPriceRange}
                        className="mb-4"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>${priceRange[0]}</span>
                        <span>${priceRange[1]}+</span>
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-8 btn-primary"
                    onClick={() => setFiltersOpen(false)}
                  >
                    Apply Filters
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Products Grid */}
          <div className="flex-1">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-20">
                <h3 className="font-serif text-2xl text-nexa-charcoal mb-2">No products found</h3>
                <p className="text-muted-foreground">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="products-grid">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
